import { useQuery } from "@tanstack/react-query";
import { AdminStatsCard } from "@/components/admin/AdminStatsCard";
import { supabase } from "@/lib/supabase";
import {
  Users,
  Calculator,
  FileText,
  DollarSign,
  Percent,
  Clock,
  MessageSquare,
  Bell,
  TrendingUp,
  Calendar,
  Database,
  Activity
} from "lucide-react";

interface Calculation {
  monthly_savings: number;
  yearly_savings: number;
  reduction_percentage: number;
}

interface PaymentPlan {
  amount_agreed: number;
  amount_paid: number;
  amount_outstanding: number;
}

const AdminMetrics = () => {
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['admin-metrics'],
    queryFn: async () => {
      const [
        { count: totalClients },
        { count: activeClients },
        { count: totalDocuments },
        { count: totalCalculations },
        { data: calculations },
        { count: totalNotifications },
        { data: paymentPlans },
        { count: totalNotes },
      ] = await Promise.all([
        supabase.from('clients').select('*', { count: 'exact', head: true }),
        supabase.from('clients').select('*', { count: 'exact', head: true }).eq('status', 'Active'),
        supabase.from('documents').select('*', { count: 'exact', head: true }),
        supabase.from('calculations').select('*', { count: 'exact', head: true }),
        supabase.from('calculations').select('monthly_savings, yearly_savings, reduction_percentage'),
        supabase.from('notifications').select('*', { count: 'exact', head: true }),
        supabase.from('payment_plans').select('amount_agreed, amount_paid, amount_outstanding'),
        supabase.from('client_notes').select('*', { count: 'exact', head: true }),
      ]);

      // Calculate averages and totals with proper null checks
      const avgMonthlySavings = calculations?.reduce((acc, curr) => acc + Number(curr.monthly_savings), 0) / (calculations?.length || 1);
      const avgYearlySavings = calculations?.reduce((acc, curr) => acc + Number(curr.yearly_savings), 0) / (calculations?.length || 1);
      const avgReductionPercentage = calculations?.reduce((acc, curr) => acc + Number(curr.reduction_percentage), 0) / (calculations?.length || 1);
      
      const totalPaymentPlans = paymentPlans?.reduce((acc, curr) => acc + Number(curr.amount_agreed), 0) || 0;
      const totalCollected = paymentPlans?.reduce((acc, curr) => acc + Number(curr.amount_paid), 0) || 0;
      const totalOutstanding = paymentPlans?.reduce((acc, curr) => acc + Number(curr.amount_outstanding), 0) || 0;

      return {
        totalClients: totalClients || 0,
        activeClients: activeClients || 0,
        totalDocuments: totalDocuments || 0,
        totalCalculations: totalCalculations || 0,
        avgMonthlySavings: avgMonthlySavings || 0,
        avgYearlySavings: avgYearlySavings || 0,
        avgReductionPercentage: avgReductionPercentage || 0,
        totalPaymentPlans,
        totalCollected,
        totalOutstanding,
        totalNotifications: totalNotifications || 0,
        totalNotes: totalNotes || 0,
      };
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold text-white">Performance Metrics</h1>
      
      {/* Client Metrics */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Client Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <AdminStatsCard
            icon={Users}
            label="Total Clients"
            value={metrics?.totalClients || 0}
            iconColor="text-blue-500"
          />
          <AdminStatsCard
            icon={Users}
            label="Active Clients"
            value={metrics?.activeClients || 0}
            iconColor="text-green-500"
          />
          <AdminStatsCard
            icon={Activity}
            label="Client Conversion Rate"
            value={`${((metrics?.activeClients / metrics?.totalClients) * 100 || 0).toFixed(1)}%`}
            iconColor="text-purple-500"
          />
        </div>
      </div>

      {/* Financial Metrics */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Financial Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <AdminStatsCard
            icon={DollarSign}
            label="Total Payment Plans Value"
            value={`R${metrics?.totalPaymentPlans.toLocaleString()}`}
            iconColor="text-green-500"
          />
          <AdminStatsCard
            icon={DollarSign}
            label="Total Collected"
            value={`R${metrics?.totalCollected.toLocaleString()}`}
            iconColor="text-blue-500"
          />
          <AdminStatsCard
            icon={DollarSign}
            label="Total Outstanding"
            value={`R${metrics?.totalOutstanding.toLocaleString()}`}
            iconColor="text-red-500"
          />
        </div>
      </div>

      {/* Savings Metrics */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Savings Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <AdminStatsCard
            icon={Calculator}
            label="Average Monthly Savings"
            value={`R${Math.round(metrics?.avgMonthlySavings).toLocaleString()}`}
            iconColor="text-blue-500"
          />
          <AdminStatsCard
            icon={Calculator}
            label="Average Yearly Savings"
            value={`R${Math.round(metrics?.avgYearlySavings).toLocaleString()}`}
            iconColor="text-green-500"
          />
          <AdminStatsCard
            icon={Percent}
            label="Average Reduction"
            value={`${metrics?.avgReductionPercentage.toFixed(1)}%`}
            iconColor="text-purple-500"
          />
        </div>
      </div>

      {/* Activity Metrics */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Activity Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <AdminStatsCard
            icon={FileText}
            label="Total Documents"
            value={metrics?.totalDocuments || 0}
            iconColor="text-yellow-500"
          />
          <AdminStatsCard
            icon={MessageSquare}
            label="Total Notes"
            value={metrics?.totalNotes || 0}
            iconColor="text-indigo-500"
          />
          <AdminStatsCard
            icon={Bell}
            label="Total Notifications"
            value={metrics?.totalNotifications || 0}
            iconColor="text-pink-500"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminMetrics;